import React from 'react';
import { Share2 } from 'lucide-react';

export const MAX_COMPARE = 4;

const longDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const PeriodPicker = ({ snapshots, selected, onToggle, onClear }) => {
  const [open, setOpen] = React.useState(false);
  const atCap = selected.length >= MAX_COMPARE;
  const selectedList = snapshots.filter((s) => selected.includes(s.id));

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="period-picker">
      <div className="flex flex-col">
        <span className="overline mb-1">Compare reporting dates</span>
        <span className="text-[11px] text-[#787872] font-mono">
          {selected.length} of max {MAX_COMPARE} selected · synced with Data Entry
        </span>
      </div>

      <div className="relative">
        <button
          className="btn-secondary"
          onClick={() => setOpen((o) => !o)}
          data-testid="period-picker-trigger"
        >
          <span className="font-heading font-semibold text-[13px]">
            {selected.length === 0 ? 'Select reporting dates…' : `${selected.length} date${selected.length === 1 ? '' : 's'}`}
          </span>
          <svg width="10" height="10" viewBox="0 0 10 10" className="ml-1"><path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" /></svg>
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <div
              className="absolute z-40 mt-1 min-w-[280px] bg-white border border-border shadow-[0_8px_24px_rgba(0,0,0,0.08)] max-h-[320px] overflow-y-auto"
              data-testid="period-picker-menu"
            >
              <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                <span className="overline">Reporting dates</span>
                <button
                  className="text-[11px] font-mono text-[#575757] hover:text-[hsl(var(--destructive))]"
                  onClick={() => onClear()}
                  data-testid="period-picker-clear"
                >
                  Clear
                </button>
              </div>
              {snapshots.length === 0 && (
                <div className="px-3 py-6 text-xs text-[#AAA]">No reporting dates yet. Create one from Data Entry.</div>
              )}
              {snapshots.map((s) => {
                const active = selected.includes(s.id);
                const disabled = !active && atCap;
                return (
                  <button
                    key={s.id}
                    disabled={disabled}
                    onClick={() => onToggle(s.id)}
                    data-testid={`period-option-${s.id}`}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 border-b border-border last:border-b-0 transition-colors ${
                      disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#EFEFEA]'
                    } ${active ? 'bg-[#F1F6F2]' : ''}`}
                  >
                    <span className={`w-3.5 h-3.5 rounded-[2px] border ${active ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))]' : 'border-[#B0B0A8]'}  flex items-center justify-center`}>
                      {active && (
                        <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                          <path d="M2 5l2 2 4-4" stroke="white" strokeWidth="1.6" />
                        </svg>
                      )}
                    </span>
                    <span className="font-heading font-semibold text-[13px] flex-1 inline-flex items-center gap-1.5">
                      {longDate(s.date)}
                      {s.shared_from && (
                        <span className="chip chip-warning text-[8px] !py-0 !px-1 leading-none" title={`Shared by ${s.shared_from.email} (${s.shared_role})`}>
                          <Share2 size={8} /> shared
                        </span>
                      )}
                    </span>
                    {disabled && <span className="text-[10px] text-[#787872] font-mono">max {MAX_COMPARE}</span>}
                  </button>
                );
              })}
              {atCap && (
                <div className="px-3 py-2 bg-[#FBF6EA] text-[11px] font-mono text-[#7A5617] border-t border-[#E7D4A8]">
                  Limit reached · deselect one to pick another
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {selectedList.map((s) => (
          <span
            key={s.id}
            className="chip"
            style={{ background: '#1A3626', color: '#fff', borderColor: '#1A3626' }}
            data-testid={`period-chip-${s.id}`}
            title={s.shared_from ? `Shared by ${s.shared_from.email} (${s.shared_role})` : undefined}
          >
            {longDate(s.date)}
            {s.shared_from && <Share2 size={9} className="text-white/80" />}
            <button
              onClick={() => onToggle(s.id)}
              className="text-white/70 hover:text-white"
              aria-label={`Remove ${s.label}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export default PeriodPicker;
